import React, { useState, useCallback, useMemo, useEffect } from "react";
import { 
  X, Maximize2, Minimize2, 
  MessageSquare, BarChart3, BookOpen, Clock, 
  ArrowUpRight, Network, Plus, AlertTriangle, 
  CheckCircle, TrendingUp, User, Folder, Calendar,
  Loader2, Bot, ChevronUp, ChevronDown, RefreshCw
} from "lucide-react";

interface ServiceDeskWorkflowAppProps {
  isOpen: boolean;
  onClose: () => void;
  onInvokeTool: (tool: string, params: any) => Promise<any>;
}

// -------------------------------------------------------------
// Sub-components for each view
// -------------------------------------------------------------

// 1. Self-Service Desk Chat Mock View
const SelfServiceView = React.memo(({ onInvokeTool, onSwitchTab, setPrefillTicket, onSelectTicket }: any) => {
  const [messages, setMessages] = useState<any[]>([
    { sender: "AI", text: "Hi! I'm your QCB IT Support assistant. How can I help you today?" }
  ]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || isLoading) return;

    const userText = input.trim();
    setMessages(prev => [...prev, { sender: "User", text: userText }]);
    setInput("");
    setIsLoading(true);

    try {
      const resp = await onInvokeTool("search_service_desk_kb", { query: userText });
      const aiText = resp.raw_response || "Sorry, I couldn't find a solution in the Knowledge Base.";
      setMessages(prev => [...prev, { sender: "AI", text: aiText, actions: resp.actions }]);
    } catch (err) {
      setMessages(prev => [...prev, { sender: "AI", text: "Error communicating with service desk tools." }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleActionClick = async (action: any) => {
    const serviceName = action.service || "chartiq";
    const displayService = serviceName.toUpperCase();
    if (action.type === "restart") {
      setMessages(prev => [...prev, { sender: "User", text: `Please restart the ${displayService} service.` }]);
      setIsLoading(true);
      try {
        const resp = await onInvokeTool("restart_gcp_service", { service_name: serviceName });
        const aiText = resp.raw_response || resp.message || "Service restarted successfully.";
        setMessages(prev => [...prev, { sender: "AI", text: aiText }]);
      } catch (err) {
        setMessages(prev => [...prev, { sender: "AI", text: "Error communicating with GCP service control." }]);
      } finally {
        setIsLoading(false);
      }
    } else if (action.type === "raise_ticket") {
      setMessages(prev => [...prev, { sender: "User", text: "Raise ticket" }]);
      setIsLoading(true);
      try {
        let subject = "IT Support Request";
        let desc = "User needs assistance with an IT issue.";
        let category = "General";
        let subCategory = "Other";
        let priority = "P3";
        let severity = "Medium";

        if (serviceName !== "general") {
          subject = `${displayService} website down`;
          desc = `${displayService} website is down. GCP logs show database connection timeout (Connection timed out to DB backend).`;
          category = "Network & Connectivity";
          subCategory = "VPN & Remote Access";
          priority = "P2";
          severity = "High";
        } else {
          const lastUserMsg = messages.filter(m => m.sender === "User").pop();
          if (lastUserMsg?.text) {
            subject = lastUserMsg.text.slice(0, 50);
            desc = lastUserMsg.text;
          }
        }

        const resp = await onInvokeTool("create_service_desk_ticket", {
          subject: subject,
          description: desc,
          category: category,
          sub_category: subCategory,
          severity: severity,
          priority: priority
        });

        if (resp.status === "success" || resp.ticket_id) {
          const ticketId = resp.ticket_id;
          const slaHrs = resp.sla_target_hrs || 24;
          const aiText = `Ticket raised! This is ticket number **${ticketId}** and as per your priority the expected SLA timing is **${slaHrs}** hours.`;
          setMessages(prev => [...prev, { 
            sender: "AI", 
            text: aiText,
            ticketId: ticketId
          }]);
        } else {
          setMessages(prev => [...prev, { sender: "AI", text: "Failed to create ticket: " + (resp.message || "Unknown error") }]);
        }
      } catch (err) {
        setMessages(prev => [...prev, { sender: "AI", text: "Error communicating with service desk tools." }]);
      } finally {
        setIsLoading(false);
      }
    }
  };

  return (
    <div className="flex flex-col h-full bg-slate-50 border border-slate-200/80 rounded-xl overflow-hidden">
      <div className="bg-white px-4 py-3 border-b border-slate-200/80 flex items-center gap-2">
        <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse"></span>
        <span className="text-xs text-slate-500 font-medium font-sans">QCB · Employee Portal</span>
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-4 custom-scrollbar">
        {messages.map((m, idx) => (
          <div key={idx} className="flex flex-col gap-2">
            <div className={`flex gap-3 max-w-[85%] ${m.sender === "User" ? "ml-auto flex-row-reverse" : ""}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold shrink-0 shadow-md ${
                m.sender === "User" ? "bg-[#8A1538] text-white" : "bg-slate-200/80 text-amber-700"
              }`}>
                {m.sender === "User" ? "SA" : "AI"}
              </div>
              <div className={`text-sm py-2.5 px-4 rounded-2xl leading-relaxed whitespace-pre-wrap ${
                m.sender === "User" 
                  ? "bg-[#8A1538] text-white rounded-tr-none" 
                  : "bg-white text-slate-800 border border-slate-200/80 rounded-tl-none"
              }`}>
                {m.text}
              </div>
            </div>
            {m.ticketId && (
              <div className="ml-11 mt-1">
                <button
                  type="button"
                  onClick={() => onSelectTicket(m.ticketId)}
                  className="px-4 py-2 text-xs font-bold rounded-lg border bg-[#8A1538] text-white hover:bg-[#a01c43] hover:shadow transition-all shadow-sm flex items-center gap-1.5"
                >
                  🔍 View/Edit Ticket Detail &amp; Activity Logs
                </button>
              </div>
            )}
            {m.actions && m.actions.length > 0 && (
              <div className="flex flex-wrap gap-2.5 ml-11 mt-1">
                {m.actions.map((act: any, aIdx: number) => (
                  <button
                    key={aIdx}
                    type="button"
                    onClick={() => handleActionClick(act)}
                    className={`px-4 py-2 text-xs font-bold rounded-lg border transition-all shadow-sm ${
                      act.type === "restart"
                        ? "bg-[#8A1538] text-white border-[#8A1538] hover:bg-[#a01c43] hover:shadow"
                        : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50 hover:text-[#8A1538] hover:border-[#8A1538]/30 hover:shadow"
                    }`}
                  >
                    {act.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        ))}
        {isLoading && (
          <div className="flex gap-3 max-w-[85%]">
            <div className="w-8 h-8 rounded-full bg-slate-200/80 flex items-center justify-center text-xs font-bold text-amber-700">AI</div>
            <div className="bg-white text-slate-400 py-2.5 px-4 rounded-2xl rounded-tl-none border border-slate-200/80 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-slate-400 animate-bounce"></span>
              <span className="w-2 h-2 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: '0.2s' }}></span>
              <span className="w-2 h-2 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: '0.4s' }}></span>
            </div>
          </div>
        )}
      </div>
      <form onSubmit={handleSend} className="p-3 bg-white border-t border-slate-200/80 flex gap-2">
        <input 
          type="text" 
          value={input} 
          onChange={e => setInput(e.target.value)} 
          placeholder="Describe your IT issue (e.g. VPN disconnecting, Outlook profile crash)..."
          className="flex-1 bg-slate-50 text-sm text-slate-800 rounded-lg px-4 py-2 border border-slate-200 focus:outline-none focus:border-[#8A1538] transition-colors"
        />
        <button 
          type="submit" 
          className="px-5 py-2 bg-[#8A1538] text-white text-xs font-semibold rounded-lg hover:bg-[#a01c43] transition-colors"
        >
          Send
        </button>
      </form>
    </div>
  );
});

// 2. SLA Countdown Badge Component (Real-Time Ticking)
const SLACountdownTimer = ({ raisedDate, raisedTime, slaTargetHrs, status }: any) => {
  const [timeLeftStr, setTimeLeftStr] = useState("");
  const [isBreached, setIsBreached] = useState(false);

  useEffect(() => {
    const calculateTime = () => {
      if (!raisedDate || !raisedTime) {
        setTimeLeftStr("N/A");
        return;
      }
      
      try {
        const dateParts = raisedDate.split("-");
        const timeClean = raisedTime.split(".")[0];
        const timeParts = timeClean.split(":");
        
        if (dateParts.length >= 3 && timeParts.length >= 2) {
          const year = parseInt(dateParts[0], 10);
          const month = parseInt(dateParts[1], 10) - 1;
          const day = parseInt(dateParts[2], 10);
          const hour = parseInt(timeParts[0], 10);
          const minute = parseInt(timeParts[1], 10);
          const second = timeParts.length >= 3 ? parseInt(timeParts[2], 10) : 0;
          
          const raisedDt = new Date(year, month, day, hour, minute, second);
          const deadline = new Date(raisedDt.getTime() + (slaTargetHrs || 24.0) * 60 * 60 * 1000);
          const now = new Date();
          
          const diffMs = deadline.getTime() - now.getTime();
          
          if (diffMs <= 0) {
            setIsBreached(true);
            const elapsedMs = now.getTime() - deadline.getTime();
            const elapsedSecs = Math.floor(elapsedMs / 1000);
            const hrs = Math.floor(elapsedSecs / 3600);
            const mins = Math.floor((elapsedSecs % 3600) / 60);
            const secs = elapsedSecs % 60;
            
            if (hrs >= 24) {
              const days = Math.floor(hrs / 24);
              const remainingHrs = hrs % 24;
              setTimeLeftStr(`Breached (${days}d ${remainingHrs}h ago)`);
            } else if (hrs > 0) {
              setTimeLeftStr(`Breached (${hrs}h ${mins}m ago)`);
            } else if (mins > 0) {
              setTimeLeftStr(`Breached (${mins}m ${secs}s ago)`);
            } else {
              setTimeLeftStr(`Breached (${secs}s ago)`);
            }
          } else {
            setIsBreached(false);
            const totalSecs = Math.floor(diffMs / 1000);
            const hrs = Math.floor(totalSecs / 3600);
            const mins = Math.floor((totalSecs % 3600) / 60);
            const secs = totalSecs % 60;
            
            if (hrs >= 24) {
              const days = Math.floor(hrs / 24);
              const remainingHrs = hrs % 24;
              setTimeLeftStr(`${days}d ${remainingHrs}h`);
            } else if (hrs > 0) {
              setTimeLeftStr(`${hrs}h ${mins}m ${secs}s`);
            } else if (mins > 0) {
              setTimeLeftStr(`${mins}m ${secs}s`);
            } else {
              setTimeLeftStr(`${secs}s`);
            }
          }
        } else {
          setTimeLeftStr("N/A");
        }
      } catch (e) {
        console.error("Error calculating SLA time:", e);
        setTimeLeftStr("N/A");
      }
    };

    calculateTime();
    const interval = setInterval(calculateTime, 1000);
    return () => clearInterval(interval);
  }, [raisedDate, raisedTime, slaTargetHrs]);

  if (isBreached) {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] font-extrabold rounded-full px-2 py-0.5 bg-red-100 text-red-700 border border-red-200 animate-pulse shrink-0">
        <span className="w-1.5 h-1.5 rounded-full bg-red-500"></span>
        {timeLeftStr}
      </span>
    );
  }

  const isUrgentAmber = !timeLeftStr.includes("d") && (!timeLeftStr.includes("h") || parseInt(timeLeftStr.split("h")[0], 10) < 3);

  return (
    <span className={`inline-flex items-center gap-1 text-[10px] font-extrabold rounded-full px-2 py-0.5 border shrink-0 ${
      isUrgentAmber 
        ? "bg-amber-100 text-amber-700 border-amber-200" 
        : "bg-emerald-100 text-emerald-700 border-emerald-200"
    }`}>
      <span className={`w-1.5 h-1.5 rounded-full ${isUrgentAmber ? "bg-amber-500" : "bg-emerald-500"}`}></span>
      SLA: {timeLeftStr}
    </span>
  );
};

// 3. Triage & Routing Ticket View
const TriageView = React.memo(({ onInvokeTool, onSelectTicket }: any) => {
  const [queue, setQueue] = useState<any[]>([]);
  const [loadingQueue, setLoadingQueue] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [kept, setKept] = useState<Record<string, boolean>>({});
  const [searchQuery, setSearchQuery] = useState("");
  const [activeFilter, setActiveFilter] = useState<"all" | "flagged" | "auto_resolve" | "ok">("all");

  const fetchQueue = useCallback(async () => {
    setLoadingQueue(true);
    try {
      const res = await onInvokeTool("get_service_desk_queue", {});
      setQueue(Array.isArray(res?.tickets) ? res.tickets : []);
    } catch (e) {
      console.error("Failed to load queue:", e);
    } finally {
      setLoadingQueue(false);
    }
  }, [onInvokeTool]);

  useEffect(() => { fetchQueue(); }, [fetchQueue]);

  const reassign = async (t: any) => {
    if (!t.suggested_agent || busyId) return;
    setBusyId(t.ticket_id);
    try {
      await onInvokeTool("update_service_desk_ticket", {
        ticket_id: t.ticket_id,
        assigned_to: t.suggested_agent,
        agent_id: t.suggested_agent_id || "",
      });
      await fetchQueue();
    } catch (e) { console.error(e); }
    finally { setBusyId(null); }
  };

  const autoResolve = async (t: any) => {
    if (busyId) return;
    setBusyId(t.ticket_id);
    try {
      await onInvokeTool("update_service_desk_ticket", { ticket_id: t.ticket_id, status: "Resolved" });
      await fetchQueue();
    } catch (e) { console.error(e); }
    finally { setBusyId(null); }
  };

  const fixPriority = async (t: any) => {
    if (!t.suggested_priority || busyId) return;
    setBusyId(t.ticket_id);
    try {
      await onInvokeTool("update_service_desk_ticket", { ticket_id: t.ticket_id, priority: t.suggested_priority });
      await fetchQueue();
    } catch (e) { console.error(e); }
    finally { setBusyId(null); }
  };

  const keep = (t: any) => setKept((prev) => ({ ...prev, [t.ticket_id]: true }));

  const flaggedCount = queue.filter((t) => t.flag === "overloaded" || t.flag === "misrouted" || t.flag === "priority_review").length;
  const autoCount = queue.filter((t) => t.flag === "auto_resolve").length;
  const okCount = queue.filter((t) => t.flag === "ok").length;

  const prClass = (p: string) =>
    p === "P1" ? "bg-red-100 text-red-700" :
    p === "P2" ? "bg-orange-100 text-orange-700" :
    p === "P3" ? "bg-blue-100 text-blue-700" : "bg-slate-100 text-slate-600";

  const firstName = (n: string) => (n ? String(n).split(" ")[0] : "best agent");

  const filteredQueue = useMemo(() => {
    let list = queue;
    if (activeFilter === "flagged") {
      list = list.filter((t) => t.flag === "overloaded" || t.flag === "misrouted" || t.flag === "priority_review");
    } else if (activeFilter === "auto_resolve") {
      list = list.filter((t) => t.flag === "auto_resolve");
    } else if (activeFilter === "ok") {
      list = list.filter((t) => t.flag === "ok");
    }

    if (!searchQuery.trim()) return list;
    const q = searchQuery.toLowerCase().trim();
    return list.filter((t) => 
      (t.ticket_id && t.ticket_id.toLowerCase().includes(q)) ||
      (t.subject && t.subject.toLowerCase().includes(q)) ||
      (t.category && t.category.toLowerCase().includes(q)) ||
      (t.sub_category && t.sub_category.toLowerCase().includes(q)) ||
      (t.assigned_to && t.assigned_to.toLowerCase().includes(q)) ||
      (t.status && t.status.toLowerCase().includes(q)) ||
      (t.priority && t.priority.toLowerCase().includes(q))
    );
  }, [queue, searchQuery, activeFilter]);

  return (
    <div className="h-full flex flex-col">
      <div className="flex items-start justify-between mb-3">
        <div>
          <h2 className="text-base font-bold text-slate-800">Live Queue &amp; Triage</h2>
          <p className="text-xs text-slate-500 mt-0.5 max-w-2xl">Tickets auto-assign on arrival (workload-aware). The team only steps in where AI flags a risk &mdash; owner overload, mis-routing, or an auto-resolvable request.</p>
        </div>
        <button onClick={fetchQueue} className="p-1.5 rounded-md hover:bg-slate-100 text-slate-400 hover:text-slate-700 transition" title="Refresh">
          <RefreshCw className={`w-4 h-4 ${loadingQueue ? "animate-spin" : ""}`} />
        </button>
      </div>

      <div className="grid grid-cols-4 gap-3 mb-3">
        <div 
          onClick={() => setActiveFilter("all")} 
          className={`cursor-pointer transition-all border rounded-xl px-4 py-3 shadow-sm ${
            activeFilter === "all" ? "border-[#8A1538] ring-1 ring-[#8A1538]/20 bg-[#8A1538]/5" : "bg-white border-slate-200 hover:border-slate-300 hover:shadow"
          }`}
        >
          <div className="text-xl font-extrabold text-slate-800">{queue.length}</div>
          <div className="text-[11px] text-slate-500 mt-0.5">Open in queue</div>
        </div>
        <div 
          onClick={() => setActiveFilter("flagged")} 
          className={`cursor-pointer transition-all border rounded-xl px-4 py-3 shadow-sm ${
            activeFilter === "flagged" ? "border-[#8A1538] ring-1 ring-[#8A1538]/20 bg-[#8A1538]/5" : "bg-white border-slate-200 hover:border-slate-300 hover:shadow"
          }`}
        >
          <div className="text-xl font-extrabold text-amber-600">{flaggedCount}</div>
          <div className="text-[11px] text-slate-500 mt-0.5">Flagged for review</div>
        </div>
        <div 
          onClick={() => setActiveFilter("auto_resolve")} 
          className={`cursor-pointer transition-all border rounded-xl px-4 py-3 shadow-sm ${
            activeFilter === "auto_resolve" ? "border-[#8A1538] ring-1 ring-[#8A1538]/20 bg-[#8A1538]/5" : "bg-white border-slate-200 hover:border-slate-300 hover:shadow"
          }`}
        >
          <div className="text-xl font-extrabold text-emerald-600">{autoCount}</div>
          <div className="text-[11px] text-slate-500 mt-0.5">Auto-resolvable</div>
        </div>
        <div 
          onClick={() => setActiveFilter("ok")} 
          className={`cursor-pointer transition-all border rounded-xl px-4 py-3 shadow-sm ${
            activeFilter === "ok" ? "border-[#8A1538] ring-1 ring-[#8A1538]/20 bg-[#8A1538]/5" : "bg-white border-slate-200 hover:border-slate-300 hover:shadow"
          }`}
        >
          <div className="text-xl font-extrabold text-slate-800">{okCount}</div>
          <div className="text-[11px] text-slate-500 mt-0.5">Healthy / auto-assigned</div>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl flex-1 overflow-hidden flex flex-col shadow-sm">
        <div className="px-4 py-2.5 border-b border-slate-200 flex items-center justify-between gap-4">
          <span className="text-[11px] font-bold tracking-wide text-slate-500 shrink-0">ACTIVE IT SUPPORT LOGS</span>
          
          <div className="flex-1 max-w-xs relative">
            <input 
              type="text" 
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="Search ID, subject, agent, category..."
              className="w-full bg-slate-50 text-[11px] text-slate-800 rounded px-2.5 py-1 border border-slate-200 focus:outline-none focus:border-[#8A1538] focus:bg-white transition-all font-sans"
            />
            {searchQuery && (
              <button 
                onClick={() => setSearchQuery("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 text-[10px] font-bold"
              >
                Clear
              </button>
            )}
          </div>

          <span className="text-[10px] text-slate-400 bg-slate-100 rounded-full px-2 py-0.5 font-bold shrink-0">
            {searchQuery.trim() ? `${filteredQueue.length} of ${queue.length}` : queue.length} open
          </span>
        </div>
        
        <div className="flex-1 overflow-y-auto p-2 space-y-2 custom-scrollbar">
          {loadingQueue && queue.length === 0 && (
            <div className="flex items-center justify-center py-12 text-slate-400 text-sm gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading queue&hellip;</div>
          )}
          {filteredQueue.map((t) => (
            <div key={t.ticket_id} className="border border-slate-200 rounded-xl p-3.5 hover:border-[#8A1538]/30 transition">
              <div onClick={() => onSelectTicket(t.ticket_id)} className="cursor-pointer group">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-extrabold text-[#8A1538] text-[13px] group-hover:underline shrink-0">{t.ticket_id}</span>
                  <SLACountdownTimer 
                    raisedDate={t.raised_date}
                    raisedTime={t.raised_time}
                    slaTargetHrs={t.sla_target_hrs}
                    status={t.status}
                  />
                  <span className="font-semibold text-[13px] text-slate-800 group-hover:underline">{t.subject}</span>
                  <span className={`text-[10px] font-extrabold rounded px-1.5 py-0.5 ${prClass(t.priority)} shrink-0`}>{t.priority}</span>
                  <span className="text-[10px] font-bold rounded-full px-2 py-0.5 bg-amber-50 text-amber-700 shrink-0">{t.status}</span>
                </div>
                <div className="flex gap-3 text-[11.5px] text-slate-500 mt-1.5 flex-wrap">
                  <span className="inline-flex items-center gap-1"><Folder className="w-3 h-3" /> {t.category} / {t.sub_category}</span>
                  <span className="inline-flex items-center gap-1"><User className="w-3 h-3" /> {t.assigned_to}</span>
                </div>
              </div>

              {kept[t.ticket_id] ? (
                <div className="mt-2.5 rounded-lg px-3 py-2 bg-slate-50 border border-slate-200 flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-slate-400 shrink-0" />
                  <span className="text-[12px] text-slate-500 italic">Kept as-is by the team &mdash; {t.assigned_to}, {t.priority}.</span>
                </div>
              ) : (
              <>
              {t.flag === "ok" && (
                <div className="mt-2.5 rounded-lg px-3 py-2 bg-emerald-50/60 border border-emerald-100 flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-emerald-600 shrink-0" />
                  <span className="text-[12px] text-slate-600">Auto-assigned to <b className="text-slate-800">{t.assigned_to}</b>{t.assigned_util != null ? ` (${t.assigned_util}% load)` : ""} &mdash; no action needed</span>
                </div>
              )}

              {(t.flag === "overloaded" || t.flag === "misrouted") && (
                <div className="mt-2.5 rounded-lg px-3 py-2.5 bg-amber-50 border border-amber-200">
                  <div className="flex items-center gap-1.5 text-[10.5px] font-extrabold uppercase tracking-wide text-amber-700 mb-1.5">
                    <AlertTriangle className="w-3.5 h-3.5" /> {t.flag === "overloaded" ? "AI flag: owner overloaded" : "AI flag: mis-routed"}
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[11.5px] bg-white border border-amber-100 rounded-md px-2 py-1 text-slate-600">{t.reason}</span>
                    {t.suggested_agent && (
                      <span className="text-[11.5px] bg-white border border-amber-100 rounded-md px-2 py-1 text-slate-600">Best fit: <b className="text-slate-800">{t.suggested_agent}</b>{t.suggested_util != null ? ` (${t.suggested_util}% load)` : ""}</span>
                    )}
                    <div className="ml-auto flex gap-2">
                      <button onClick={() => reassign(t)} disabled={busyId === t.ticket_id || !t.suggested_agent} className="px-3 py-1.5 text-[11.5px] font-bold rounded-lg bg-[#8A1538] text-white hover:bg-[#a01c43] disabled:opacity-60 inline-flex items-center gap-1">
                        {busyId === t.ticket_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ArrowUpRight className="w-3.5 h-3.5" />}
                        {t.flag === "overloaded" ? "Reassign to " : "Route to "}{firstName(t.suggested_agent)}
                      </button>
                      <button onClick={() => keep(t)} className="px-3 py-1.5 text-[11.5px] font-bold rounded-lg bg-white text-slate-600 border border-slate-200 hover:border-slate-300">Keep</button>
                    </div>
                  </div>
                </div>
              )}

              {t.flag === "auto_resolve" && (
                <div className="mt-2.5 rounded-lg px-3 py-2.5 bg-emerald-50 border border-emerald-200">
                  <div className="flex items-center gap-1.5 text-[10.5px] font-extrabold uppercase tracking-wide text-emerald-700 mb-1.5">
                    <CheckCircle className="w-3.5 h-3.5" /> Auto-resolve eligible
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[11.5px] bg-white border border-emerald-100 rounded-md px-2 py-1 text-slate-600">{t.reason}</span>
                    <div className="ml-auto flex gap-2">
                      <button onClick={() => autoResolve(t)} disabled={busyId === t.ticket_id} className="px-3 py-1.5 text-[11.5px] font-bold rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-60 inline-flex items-center gap-1">
                        {busyId === t.ticket_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle className="w-3.5 h-3.5" />} Auto-resolve &amp; notify
                      </button>
                      <button onClick={() => keep(t)} className="px-3 py-1.5 text-[11.5px] font-bold rounded-lg bg-white text-slate-600 border border-slate-200 hover:border-slate-300">Keep open</button>
                    </div>
                  </div>
                </div>
              )}

              {t.flag === "priority_review" && (
                <div className="mt-2.5 rounded-lg px-3 py-2.5 bg-purple-50 border border-purple-200">
                  <div className="flex items-center gap-1.5 text-[10.5px] font-extrabold uppercase tracking-wide text-purple-700 mb-1.5">
                    <AlertTriangle className="w-3.5 h-3.5" /> AI flag: priority looks overstated
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[11.5px] bg-white border border-purple-100 rounded-md px-2 py-1 text-slate-600">{t.reason}</span>
                    <div className="ml-auto flex gap-2">
                      <button onClick={() => fixPriority(t)} disabled={busyId === t.ticket_id} className="px-3 py-1.5 text-[11.5px] font-bold rounded-lg bg-[#8A1538] text-white hover:bg-[#a01c43] disabled:opacity-60 inline-flex items-center gap-1">
                        {busyId === t.ticket_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ArrowUpRight className="w-3.5 h-3.5" />} Set to {t.suggested_priority}
                      </button>
                      <button onClick={() => keep(t)} className="px-3 py-1.5 text-[11.5px] font-bold rounded-lg bg-white text-slate-600 border border-slate-200 hover:border-slate-300">Keep {t.priority}</button>
                    </div>
                  </div>
                </div>
              )}
              </>
              )}
            </div>
          ))}
          {!loadingQueue && filteredQueue.length === 0 && (
            <div className="text-center text-slate-400 text-sm py-12">
              {queue.length === 0 ? "No open tickets in the queue." : "No matching tickets found."}
            </div>
          )}
        </div>
      </div>
    </div>
  );
});

// 3. Knowledge Base View (RAG)
const KnowledgeBaseView = React.memo(({ onInvokeTool }: any) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [articles, setArticles] = useState<any[]>([]);
  const [aiAnswer, setAiAnswer] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!searchTerm.trim()) return;
    setLoading(true);
    try {
      const resp = await onInvokeTool("search_service_desk_kb", { query: searchTerm });
      setArticles(Array.isArray(resp.results) ? resp.results : []);
      setAiAnswer(resp.raw_response || "");
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col h-full bg-white border border-slate-200/80 rounded-xl overflow-hidden p-5">
      <form onSubmit={handleSearch} className="flex gap-2 mb-6">
        <input 
          type="text" 
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          placeholder="Search corporate IT knowledge base (e.g. password expired, T24 banking error)..."
          className="flex-1 bg-slate-50 text-xs text-slate-800 rounded px-4 py-2.5 border border-slate-200 focus:outline-none focus:border-[#8A1538]"
        />
        <button 
          type="submit"
          className="px-6 bg-[#8A1538] hover:bg-[#a01c43] text-white text-xs font-semibold rounded transition-colors"
        >
          Search
        </button>
      </form>

      {loading ? (
        <div className="flex-1 flex flex-col items-center justify-center text-slate-400">
          <Loader2 className="w-8 h-8 animate-spin mb-2" />
          <span className="text-xs">Searching database and calling RAG context...</span>
        </div>
      ) : (
        <div className="flex-1 grid grid-cols-1 lg:grid-cols-2 gap-6 overflow-hidden">
          {/* Left Panel: AI Guided Resolution */}
          <div className="flex flex-col h-full bg-slate-50 border border-slate-200/80 rounded-lg p-4 overflow-y-auto custom-scrollbar">
            <div className="flex items-center gap-2 mb-3 pb-2 border-b border-slate-200/60">
              <span className="text-xs font-bold text-amber-700 bg-amber-400/10 px-2 py-0.5 rounded border border-amber-400/20">AI Generated Solution</span>
            </div>
            {aiAnswer ? (
              <div className="text-sm text-slate-800 leading-relaxed whitespace-pre-wrap font-sans">
                {aiAnswer}
              </div>
            ) : (
              <div className="flex-1 flex flex-col items-center justify-center text-slate-400 text-xs">
                <Bot className="w-10 h-10 mb-2 opacity-30" />
                <span>Search above to generate AI-assisted steps.</span>
              </div>
            )}
          </div>

          {/* Right Panel: Found Articles Details */}
          <div className="flex flex-col h-full bg-slate-50 border border-slate-200/80 rounded-lg p-4 overflow-y-auto custom-scrollbar space-y-4">
            <h5 className="text-xs font-bold text-slate-600 uppercase tracking-wider pb-2 border-b border-slate-200/60">Matching KB Articles</h5>
            {articles.length > 0 ? (
              articles.map(art => (
                <div key={art.id} className="p-3 bg-white rounded-lg border border-slate-200/60">
                  <div className="flex justify-between items-start gap-2 mb-1.5">
                    <h6 className="text-xs font-bold text-[#8A1538]">{art.id} · {art.title}</h6>
                    <span className="text-[9px] font-semibold text-slate-500 px-1.5 py-0.5 bg-slate-100 rounded">
                      {art.severity} Severity
                    </span>
                  </div>
                  <div className="text-[11px] text-slate-600 leading-normal mb-2">
                    <p className="font-semibold text-slate-500 mb-0.5">Symptoms:</p>
                    <p className="line-clamp-2 text-slate-600 mb-2">{art.symptoms}</p>
                    
                    <p className="font-semibold text-slate-500 mb-0.5">Resolution Guide:</p>
                    <p className="whitespace-pre-line text-slate-800 bg-slate-50 p-2 rounded text-[10px] font-mono leading-relaxed border border-slate-200/60">{art.resolution}</p>
                  </div>
                </div>
              ))
            ) : (
              <div className="flex-1 flex items-center justify-center text-slate-500 text-xs">
                No matching source articles loaded.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
});

// 4. SLA Watchdog View
const SLAWatchdogView = React.memo(({ onInvokeTool, onSelectTicket }: any) => {
  const [loading, setLoading] = useState(false);
  const [slaData, setSlaData] = useState<any>(null);

  const fetchSLA = useCallback(async () => {
    setLoading(true);
    try {
      const res = await onInvokeTool("get_qcb_sla_compliance", {});
      setSlaData(res);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [onInvokeTool]);

  useEffect(() => {
    fetchSLA();
  }, [fetchSLA]);

  useEffect(() => {
    const handleUpdate = () => {
      fetchSLA();
    };
    window.addEventListener("ticketUpdated", handleUpdate);
    return () => {
      window.removeEventListener("ticketUpdated", handleUpdate);
    };
  }, [fetchSLA]);

  const urgentBreaches = useMemo(() => {
    if (!slaData || !Array.isArray(slaData.open_tickets)) return [];
    return slaData.open_tickets.filter((t: any) => t.risk_pct >= 90.0 || t.breached);
  }, [slaData]);

  return (
    <div className="flex flex-col h-full bg-white border border-slate-200/80 rounded-xl overflow-hidden p-5">
      <div className="flex justify-between items-center mb-5 border-b border-slate-200/60 pb-3">
        <h4 className="text-sm font-semibold text-slate-800 flex items-center gap-2">
          <Clock className="w-5 h-5 text-amber-600" />
          SLA Watchdog Countdown Monitor
        </h4>
        <button onClick={fetchSLA} className="p-1 text-slate-500 hover:text-slate-800 transition-colors">
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      {loading && !slaData ? (
        <div className="flex-1 flex flex-col items-center justify-center text-slate-400">
          <Loader2 className="w-8 h-8 animate-spin mb-2" />
          <span className="text-xs">Analyzing SLA clocks...</span>
        </div>
      ) : (
        <div className="flex-1 grid grid-cols-1 lg:grid-cols-3 gap-6 overflow-hidden">
          {/* Left 2 Cols: Open Ticket countdowns */}
          <div className="lg:col-span-2 flex flex-col h-full overflow-hidden">
            {urgentBreaches.length > 0 && (
              <div className="mb-4 bg-red-50 border border-red-200 p-3 rounded-lg flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-red-500 shrink-0 mt-0.5 animate-pulse" />
                <div className="text-xs text-red-700">
                  <p className="font-semibold text-red-600">Predicted SLA Breaches / Alerts Detected!</p>
                  <p className="mt-0.5">
                    {urgentBreaches[0].ticket_id} ({urgentBreaches[0].subject}) has {urgentBreaches[0].time_left} left. 
                    Auto-escalating ticket assignments to specialized queues.
                  </p>
                </div>
              </div>
            )}

            <div className="flex-1 bg-slate-50 border border-slate-200/80 rounded-lg overflow-y-auto custom-scrollbar">
              <div className="p-3 bg-slate-100 text-[10px] font-bold text-slate-500 uppercase tracking-wider grid grid-cols-4 gap-2 border-b border-slate-200/60">
                <div className="col-span-2">Open Ticket</div>
                <div className="text-right">Time Remaining</div>
                <div className="text-right">Breach Risk</div>
              </div>
              
              <div className="divide-y divide-slate-200/60">
                {slaData?.open_tickets?.map((t: any) => (
                  <div 
                    key={t.ticket_id} 
                    onClick={() => onSelectTicket(t.ticket_id)}
                    className="p-3 text-xs grid grid-cols-4 gap-2 items-center hover:bg-slate-100/40 cursor-pointer border-l-2 border-transparent hover:border-[#8A1538] hover:shadow-sm transition-all"
                  >
                    <div className="col-span-2">
                      <p className="font-semibold text-slate-800 line-clamp-1">{t.ticket_id} · {t.subject}</p>
                      <p className="text-[10px] text-slate-500">Assignee: {t.assigned_to}</p>
                    </div>
                    <div className={`text-right font-medium ${t.breached ? "text-red-600 font-bold" : t.risk_pct > 75 ? "text-amber-600" : "text-emerald-600"}`}>
                      {t.time_left}
                    </div>
                    <div className="flex justify-end items-center gap-2">
                      <span className={`font-semibold ${t.breached ? "text-red-600" : t.risk_pct > 75 ? "text-amber-600" : "text-emerald-600"}`}>
                        {t.risk_pct}%
                      </span>
                      <div className="w-12 bg-slate-200 h-2 rounded-full overflow-hidden shrink-0">
                        <div 
                          className={`h-full rounded-full ${t.breached ? "bg-red-500" : t.risk_pct > 75 ? "bg-amber-500" : "bg-emerald-500"}`} 
                          style={{ width: `${t.risk_pct}%` }} 
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right Col: Priority SLA Tiers progress */}
          <div className="bg-slate-50 border border-slate-200/80 rounded-lg p-4 flex flex-col justify-between">
            <div>
              <h5 className="text-xs font-bold text-slate-600 uppercase tracking-wider mb-4 pb-2 border-b border-slate-200/60">Monthly SLA Tier Targets</h5>
              <div className="space-y-4">
                {slaData?.sla_summary?.map((s: any) => (
                  <div key={s.priority} className="space-y-1">
                    <div className="flex justify-between items-center text-xs">
                      <span className="font-semibold text-slate-700">{s.priority} Ticket SLA</span>
                      <span className={`font-bold ${s.compliance_rate_pct >= 95 ? "text-emerald-600" : s.compliance_rate_pct >= 90 ? "text-amber-600" : "text-red-600"}`}>
                        {s.compliance_rate_pct}%
                      </span>
                    </div>
                    <div className="w-full h-2.5 bg-slate-200/60 rounded-full overflow-hidden border border-slate-300/40">
                      <div 
                        className={`h-full rounded-full ${s.compliance_rate_pct >= 95 ? "bg-emerald-500" : s.compliance_rate_pct >= 90 ? "bg-amber-500" : "bg-red-500"}`} 
                        style={{ width: `${s.compliance_rate_pct}%` }} 
                      />
                    </div>
                    <div className="flex justify-between text-[9px] text-slate-500">
                      <span>Target: {s.sla_target_hrs} hrs</span>
                      <span>Resolved: {s.sla_met}/{s.total_tickets}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="bg-slate-100/60 border border-[#8A1538]/10 p-3 rounded-lg text-[11px] text-slate-600 leading-normal">
              <span className="font-bold text-[#8A1538] block mb-1">Watchdog Escalations:</span>
              Breaches are model-predicted using active queues and ticket complexities. Highly critical tasks are routed immediately to the Level 2 NOC/Incident teams.
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

// 5. Operations Dashboard View
const OpsDashboardView = React.memo(({ onInvokeTool, onSelectTicket }: any) => {
  const [stats, setStats] = useState<any>(null);
  const [sla, setSla] = useState<any>(null);
  const [metrics, setMetrics] = useState<any>(null);
  const [clustering, setClustering] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [selectedCluster, setSelectedCluster] = useState<number | null>(null);
  const [hoveredSub, setHoveredSub] = useState<any | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const [s, sl, m, c] = await Promise.allSettled([
      onInvokeTool("get_qcb_dashboard_stats", {}),
      onInvokeTool("get_qcb_sla_compliance", {}),
      onInvokeTool("get_qcb_dashboard_metrics", {}),
      onInvokeTool("get_qcb_subcategory_clustering", {}),
    ]);
    if (s.status === "fulfilled") setStats(s.value);
    if (sl.status === "fulfilled") setSla(sl.value);
    if (m.status === "fulfilled") setMetrics(m.value);
    if (c.status === "fulfilled") setClustering(c.value);
    setLoading(false);
  }, [onInvokeTool]);

  useEffect(() => { load(); }, [load]);

  const kStats = stats?.stats || {};
  const insights = stats?.insights || {};
  const slaSummary: any[] = Array.isArray(sla?.sla_summary) ? sla.sla_summary : [];
  const breachRisk: any[] = Array.isArray(sla?.open_tickets) ? sla.open_tickets.slice(0, 6) : [];
  const statusCounts: Record<string, number> = metrics?.status_counts || {};
  const departments: any[] = Array.isArray(metrics?.departments) ? metrics.departments : [];
  const agents: any[] = Array.isArray(metrics?.agents) ? metrics.agents.slice(0, 8) : [];
  const categories: Record<string, number> = kStats.categories || {};
  const catEntries = Object.entries(categories).sort((a: any, b: any) => b[1] - a[1]);
  const catMax = catEntries.length ? Math.max(...catEntries.map((e: any) => e[1])) : 1;
  const deptMax = departments.length ? Math.max(...departments.map((d: any) => d.count)) : 1;

  const statusOrder = ["Resolved", "In Progress", "Open", "Closed", "Escalated"];
  const statusColor: Record<string, string> = { Resolved: "#16a34a", "In Progress": "#2563eb", Open: "#d97706", Closed: "#94a3b8", Escalated: "#dc2626" };
  const statusTotal = Object.values(statusCounts).reduce((a: number, b: number) => a + b, 0) || 1;
  let acc = 0; const segs: string[] = [];
  statusOrder.forEach((s) => { const v = statusCounts[s] || 0; if (v > 0) { const start = (acc / statusTotal) * 100; acc += v; const end = (acc / statusTotal) * 100; segs.push(`${statusColor[s]} ${start}% ${end}%`); } });
  const donutBg = segs.length ? `conic-gradient(${segs.join(",")})` : "#e2e8f0";

  const prClass = (p: string) =>
    p === "P1" ? "bg-red-100 text-red-700" :
    p === "P2" ? "bg-orange-100 text-orange-700" :
    p === "P3" ? "bg-blue-100 text-blue-700" : "bg-slate-100 text-slate-600";
  const slaBar = (p: number) => (p >= 85 ? "#16a34a" : p >= 70 ? "#d97706" : "#dc2626");

  if (loading && !stats && !metrics) {
    return <div className="h-full flex items-center justify-center text-slate-400 text-sm gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading operations data&hellip;</div>;
  }

  return (
    <div className="h-full overflow-y-auto custom-scrollbar pr-1">
      <div className="flex items-start justify-between mb-3">
        <div>
          <h2 className="text-base font-bold text-slate-800">Operations Dashboard</h2>
          <p className="text-xs text-slate-500 mt-0.5">Live picture of the whole service desk, built from your QCB tables.</p>
        </div>
        <button onClick={load} className="p-1.5 rounded-md hover:bg-slate-100 text-slate-400 hover:text-slate-700 transition" title="Refresh">
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
        </button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-6 gap-3 mb-4">
        <div className="bg-white border border-slate-200 rounded-xl px-3 py-3 shadow-sm"><div className="text-lg font-extrabold text-slate-800">{kStats.total_open ?? metrics?.open_tickets ?? "-"}</div><div className="text-[10.5px] text-slate-500 mt-0.5">Open tickets</div></div>
        <div className="bg-white border border-slate-200 rounded-xl px-3 py-3 shadow-sm"><div className="text-lg font-extrabold text-slate-800">{kStats.resolved_today ?? "-"}</div><div className="text-[10.5px] text-slate-500 mt-0.5">Resolved today</div></div>
        <div className="bg-white border border-slate-200 rounded-xl px-3 py-3 shadow-sm"><div className="text-lg font-extrabold text-amber-600">{slaSummary.length ? Math.round(slaSummary.reduce((a, b) => a + (b.compliance_rate_pct || 0), 0) / slaSummary.length) + "%" : "-"}</div><div className="text-[10.5px] text-slate-500 mt-0.5">SLA compliance</div></div>
        <div className="bg-white border border-slate-200 rounded-xl px-3 py-3 shadow-sm"><div className="text-lg font-extrabold text-slate-800">{kStats.csat_average ?? "-"}<span className="text-[11px] text-slate-400">/5</span></div><div className="text-[10.5px] text-slate-500 mt-0.5">CSAT average</div></div>
        <div className="bg-white border border-slate-200 rounded-xl px-3 py-3 shadow-sm"><div className="text-lg font-extrabold text-emerald-600">{kStats.ai_deflection_rate != null ? kStats.ai_deflection_rate + "%" : "-"}</div><div className="text-[10.5px] text-slate-500 mt-0.5">AI deflection</div></div>
        <div className="bg-white border border-slate-200 rounded-xl px-3 py-3 shadow-sm"><div className="text-lg font-extrabold text-slate-800">{metrics?.total_tickets ?? "-"}</div><div className="text-[10.5px] text-slate-500 mt-0.5">Total tickets</div></div>
      </div>

      {/* AI insights + breach risk */}
      <div className="grid grid-cols-3 gap-4 mb-4">
        <div className="col-span-2 bg-white border border-slate-200 rounded-xl shadow-sm">
          <div className="px-4 py-2.5 border-b border-slate-200 flex items-center"><span className="text-[11px] font-bold tracking-wide text-slate-500">AI OPERATIONAL INSIGHTS</span></div>
          {insights.recurring_pattern || insights.workload_imbalance ? (
            <div className="divide-y divide-slate-100">
              {insights.recurring_pattern && (
                <div className="px-4 py-3"><div className="flex items-center gap-2 mb-1"><span className="text-[9.5px] font-extrabold uppercase tracking-wide bg-red-100 text-red-700 rounded px-1.5 py-0.5">Recurring pattern</span><span className="font-bold text-[13px] text-slate-800">{insights.recurring_pattern.title}</span></div><p className="text-[12px] text-slate-600 leading-relaxed">{insights.recurring_pattern.text}</p></div>
              )}
              {insights.workload_imbalance && (
                <div className="px-4 py-3"><div className="flex items-center gap-2 mb-1"><span className="text-[9.5px] font-extrabold uppercase tracking-wide bg-amber-100 text-amber-700 rounded px-1.5 py-0.5">Workload imbalance</span><span className="font-bold text-[13px] text-slate-800">{insights.workload_imbalance.title}</span></div><p className="text-[12px] text-slate-600 leading-relaxed">{insights.workload_imbalance.text}</p></div>
              )}
            </div>
          ) : (
            <div className="px-4 py-6 text-center text-slate-400 text-xs flex items-center justify-center gap-2"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Generating insights&hellip;</div>
          )}
        </div>
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm">
          <div className="px-4 py-2.5 border-b border-slate-200"><span className="text-[11px] font-bold tracking-wide text-slate-500">SLA BREACH RISK</span></div>
          <div className="p-1.5">
            {breachRisk.length === 0 && <div className="text-center text-slate-400 text-xs py-6">No open tickets.</div>}
            {breachRisk.map((t: any, i: number) => {
              const breached = t.breached || String(t.time_left).toLowerCase() === "breached";
              return (
                <div key={i} onClick={() => onSelectTicket(t.ticket_id)} className="flex items-center gap-2 px-2.5 py-2 border-b border-slate-50 last:border-0 cursor-pointer hover:bg-slate-50 transition-colors rounded-lg">
                  <div className="min-w-0 flex-1"><div className="text-[12px] font-bold text-slate-700 truncate">{t.ticket_id} <span className={`text-[9px] font-extrabold rounded px-1 py-0.5 ${prClass(t.priority)}`}>{t.priority}</span></div><div className="text-[10.5px] text-slate-400 truncate">{t.subject} &middot; {t.assigned_to}</div></div>
                  <span className={`text-[11px] font-extrabold rounded-md px-2 py-1 shrink-0 ${breached ? "bg-red-100 text-red-700" : (t.risk_pct > 60 ? "bg-amber-100 text-amber-700" : "bg-emerald-50 text-emerald-700")}`}>{t.time_left}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* SLA by priority + status donut */}
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm">
          <div className="px-4 py-2.5 border-b border-slate-200"><span className="text-[11px] font-bold tracking-wide text-slate-500">SLA COMPLIANCE BY PRIORITY</span></div>
          <div className="p-4 space-y-2.5">
            {slaSummary.map((s: any) => (
              <div key={s.priority} className="flex items-center gap-3">
                <span className={`text-[11px] font-extrabold rounded px-2 py-0.5 ${prClass(s.priority)}`}>{s.priority}</span>
                <div className="flex-1 h-2.5 rounded-full bg-slate-100 overflow-hidden"><div className="h-full rounded-full" style={{ width: `${s.compliance_rate_pct}%`, background: slaBar(s.compliance_rate_pct) }} /></div>
                <span className="text-[11px] text-slate-500 w-36 text-right shrink-0">Compliance: <b className="text-slate-800">{s.compliance_rate_pct}%</b> &middot; Target: <b>{s.sla_target_hrs}h</b></span>
              </div>
            ))}
            {slaSummary.length === 0 && <div className="text-center text-slate-400 text-xs py-4">No SLA data.</div>}
          </div>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm">
          <div className="px-4 py-2.5 border-b border-slate-200 flex items-center"><span className="text-[11px] font-bold tracking-wide text-slate-500">TICKETS BY STATUS</span><span className="ml-auto text-[10px] text-slate-400 font-bold">{metrics?.total_tickets ?? statusTotal} total</span></div>
          <div className="p-4 flex items-center gap-5">
            <div className="relative w-28 h-28 rounded-full shrink-0" style={{ background: donutBg }}>
              <div className="absolute inset-[18px] bg-white rounded-full flex flex-col items-center justify-center"><span className="text-xl font-extrabold text-slate-800 leading-none">{metrics?.open_tickets || kStats.total_open || 0}</span><span className="text-[9px] text-slate-400">open</span></div>
            </div>
            <div className="flex-1 space-y-1.5">
              {statusOrder.filter((s) => statusCounts[s]).map((s) => (
                <div key={s} className="flex items-center gap-2 text-[12px]"><span className="w-2.5 h-2.5 rounded" style={{ background: statusColor[s] }} /> {s} <span className="ml-auto font-bold text-slate-700">{statusCounts[s]}</span></div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* categories + departments */}
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm">
          <div className="px-4 py-2.5 border-b border-slate-200"><span className="text-[11px] font-bold tracking-wide text-slate-500">TICKETS BY CATEGORY</span></div>
          <div className="p-4 space-y-1.5">
            {catEntries.map(([name, count]: any) => (
              <div key={name} className="grid grid-cols-[150px_1fr_36px] items-center gap-3"><span className="text-[12px] text-slate-600 font-medium truncate" title={name}>{name}</span><div className="h-2.5 rounded-full bg-slate-100 overflow-hidden"><div className="h-full rounded-full bg-[#8A1538]" style={{ width: `${Math.max(6, (count / catMax) * 100)}%` }} /></div><span className="text-[12px] font-extrabold text-right text-slate-700">{count}</span></div>
            ))}
            {catEntries.length === 0 && <div className="text-center text-slate-400 text-xs py-4">No data.</div>}
          </div>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm">
          <div className="px-4 py-2.5 border-b border-slate-200"><span className="text-[11px] font-bold tracking-wide text-slate-500">TICKETS BY DEPARTMENT</span></div>
          <div className="p-4 space-y-1.5">
            {departments.map((d: any) => (
              <div key={d.name} className="grid grid-cols-[150px_1fr_36px] items-center gap-3"><span className="text-[12px] text-slate-600 font-medium truncate" title={d.name}>{d.name}</span><div className="h-2.5 rounded-full bg-slate-100 overflow-hidden"><div className="h-full rounded-full bg-[#a01c43]" style={{ width: `${Math.max(6, (d.count / deptMax) * 100)}%` }} /></div><span className="text-[12px] font-extrabold text-right text-slate-700">{d.count}</span></div>
            ))}
            {departments.length === 0 && <div className="text-center text-slate-400 text-xs py-4">No data.</div>}
          </div>
        </div>
      </div>

      {/* K-Means Subcategory Clustering Bubble Chart */}
      {clustering && (
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm mb-4 p-4">
          <div className="border-b border-slate-200 pb-2 mb-3 flex items-center justify-between">
            <div>
              <span className="text-[11px] font-bold tracking-wide text-slate-500 uppercase">SUBCATEGORY OPERATIONAL ANALYSIS (K-MEANS CLUSTERED)</span>
              <p className="text-[10px] text-slate-400 mt-0.5">Subcategories grouped by ticket volume, CSAT, resolution speed, and SLA breach rate using K-Means.</p>
            </div>
            <button 
              onClick={() => setSelectedCluster(null)}
              className="text-[10px] font-bold text-[#8A1538] hover:underline"
              disabled={selectedCluster === null}
            >
              Clear Filter
            </button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 items-start">
            {/* SVG Bubble Chart Column */}
            <div className="lg:col-span-3 flex flex-col items-center relative bg-slate-50 rounded-xl border border-slate-100 p-2 overflow-visible">
              <span className="absolute top-2 left-2 text-[9px] font-bold text-slate-400 bg-white border border-slate-200/60 rounded px-1.5 py-0.5">X: Avg Resolution (hrs) · Y: SLA Breach (%)</span>
              <svg width="100%" height="340" viewBox={`0 0 560 320`} className="overflow-visible font-sans">
                {/* Horizontal gridlines */}
                {[0, 25, 50, 75, 100].map((yVal) => {
                  const y = 20 + 260 - (yVal / 100) * 260;
                  return (
                    <g key={yVal} className="opacity-40">
                      <line x1={50} y1={y} x2={530} y2={y} stroke="#cbd5e1" strokeWidth="1" strokeDasharray="2,2" />
                      <text x={42} y={y + 3} textAnchor="end" className="text-[9px] font-bold text-slate-400 fill-slate-400">{yVal}%</text>
                    </g>
                  );
                })}

                {/* Vertical gridlines */}
                {Array.from({ length: 6 }).map((_, idx) => {
                  const maxResTime = clustering.subcategories.length ? Math.max(...clustering.subcategories.map((s: any) => s.avg_resolution_time_hrs)) : 24;
                  const xMax = Math.max(24, Math.ceil(maxResTime / 10) * 10);
                  const xVal = idx * Math.floor(xMax / 5);
                  const x = 50 + (xVal / xMax) * 480;
                  return (
                    <g key={idx} className="opacity-40">
                      <line x1={x} y1={20} x2={x} y2={280} stroke="#cbd5e1" strokeWidth="1" strokeDasharray="2,2" />
                      <text x={x} y={294} textAnchor="middle" className="text-[9px] font-bold text-slate-400 fill-slate-400">{xVal}h</text>
                    </g>
                  );
                })}

                {/* Axes lines */}
                <line x1={50} y1={280} x2={530} y2={280} stroke="#94a3b8" strokeWidth="1.5" />
                <line x1={50} y1={20} x2={50} y2={280} stroke="#94a3b8" strokeWidth="1.5" />

                {/* Hover dashed lines */}
                {hoveredSub && (
                  <>
                    <line 
                      x1={50} 
                      y1={hoveredSub.y} 
                      x2={hoveredSub.x} 
                      y2={hoveredSub.y} 
                      stroke={["#8A1538", "#D4AF37", "#0EA5E9", "#6366F1"][hoveredSub.cluster_id]} 
                      strokeDasharray="3,3" 
                      strokeWidth="1.2" 
                    />
                    <line 
                      x1={hoveredSub.x} 
                      y1={20} 
                      x2={hoveredSub.x} 
                      y2={280} 
                      stroke={["#8A1538", "#D4AF37", "#0EA5E9", "#6366F1"][hoveredSub.cluster_id]} 
                      strokeDasharray="3,3" 
                      strokeWidth="1.2" 
                    />
                  </>
                )}

                {/* Bubbles */}
                {clustering.subcategories.map((sub: any, idx: number) => {
                  const maxResTime = clustering.subcategories.length ? Math.max(...clustering.subcategories.map((s: any) => s.avg_resolution_time_hrs)) : 24;
                  const xMax = Math.max(24, Math.ceil(maxResTime / 10) * 10);
                  const ticketCounts = clustering.subcategories.map((s: any) => s.ticket_count);
                  const minTickets = ticketCounts.length ? Math.min(...ticketCounts) : 1;
                  const maxTickets = ticketCounts.length ? Math.max(...ticketCounts) : 100;

                  const x = 50 + (sub.avg_resolution_time_hrs / xMax) * 480;
                  const y = 20 + 260 - (sub.sla_breach_rate_pct / 100) * 260;
                  const r = maxTickets === minTickets ? 12 : 6 + ((sub.ticket_count - minTickets) / (maxTickets - minTickets)) * 18;
                  
                  const isSelected = selectedCluster === null || selectedCluster === sub.cluster_id;
                  const isHovered = hoveredSub && hoveredSub.idx === idx;
                  const opacity = isSelected ? (isHovered ? 1.0 : 0.85) : 0.15;
                  const colors = ["#8A1538", "#D4AF37", "#0EA5E9", "#6366F1"];
                  
                  return (
                    <circle
                      key={idx}
                      cx={x}
                      cy={y}
                      r={r}
                      fill={colors[sub.cluster_id]}
                      stroke={isHovered ? "#fff" : colors[sub.cluster_id]}
                      strokeWidth={isHovered ? 2 : 1}
                      style={{ opacity, cursor: "pointer", transition: "all 0.2s ease-in-out" }}
                      onMouseEnter={(e) => {
                        const rect = e.currentTarget.getBoundingClientRect();
                        const svgRect = e.currentTarget.parentElement?.getBoundingClientRect();
                        const tooltipX = rect.left - (svgRect?.left || 0) + r + 5;
                        const tooltipY = rect.top - (svgRect?.top || 0) - 10;
                        setHoveredSub({
                          ...sub,
                          idx,
                          x,
                          y,
                          r,
                          tooltipX,
                          tooltipY
                        });
                      }}
                      onMouseLeave={() => setHoveredSub(null)}
                      onClick={() => setSelectedCluster(sub.cluster_id)}
                    />
                  );
                })}
              </svg>

              {/* Floating Tooltip */}
              {hoveredSub && (
                <div 
                  className="absolute bg-white/95 backdrop-blur-md border border-slate-200/80 rounded-xl p-3 shadow-xl z-20 pointer-events-none font-sans min-w-[180px] max-w-[240px] animate-in fade-in zoom-in-95 duration-150"
                  style={{
                    left: `${hoveredSub.tooltipX}px`,
                    top: `${hoveredSub.tooltipY}px`,
                  }}
                >
                  <div className="flex items-center gap-1.5 mb-1">
                    <span 
                      className="w-2.5 h-2.5 rounded-full shrink-0" 
                      style={{ background: ["#8A1538", "#D4AF37", "#0EA5E9", "#6366F1"][hoveredSub.cluster_id] }}
                    />
                    <span className="text-[10px] font-extrabold text-[#8A1538] uppercase truncate">{clustering.clusters[hoveredSub.cluster_id]?.name || `Cluster ${hoveredSub.cluster_id}`}</span>
                  </div>
                  <div className="font-bold text-xs text-slate-800 line-clamp-1">{hoveredSub.sub_category}</div>
                  <div className="text-[9.5px] text-slate-500 font-semibold mb-2">{hoveredSub.category}</div>
                  
                  <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-[10.5px] border-t border-slate-100 pt-1.5">
                    <div className="text-slate-400">Tickets:</div>
                    <div className="font-bold text-right text-slate-800">{hoveredSub.ticket_count}</div>
                    
                    <div className="text-slate-400">CSAT:</div>
                    <div className="font-bold text-right text-amber-500 font-sans">⭐ {hoveredSub.avg_csat}</div>
                    
                    <div className="text-slate-400">Avg Resolution:</div>
                    <div className="font-bold text-right text-slate-800">{hoveredSub.avg_resolution_time_hrs}h</div>
                    
                    <div className="text-slate-400">SLA Breach:</div>
                    <div className="font-bold text-right text-red-600">{hoveredSub.sla_breach_rate_pct}%</div>
                  </div>
                </div>
              )}
            </div>

            {/* Clusters Legend & Recommendation Column */}
            <div className="lg:col-span-2 space-y-3 max-h-[340px] overflow-y-auto pr-1 custom-scrollbar">
              <span className="text-[9.5px] font-bold text-slate-400 uppercase tracking-widest block">CLUSTERS &amp; AI DECISION SUPPORT</span>
              
              {Object.keys(clustering.clusters).map((cId: any) => {
                const cNum = parseInt(cId, 10);
                const cluster = clustering.clusters[cId];
                const isSelected = selectedCluster === cNum;
                const isAnySelected = selectedCluster !== null;
                const colors = ["#8A1538", "#D4AF37", "#0EA5E9", "#6366F1"];
                
                return (
                  <div 
                    key={cId}
                    onClick={() => setSelectedCluster(isSelected ? null : cNum)}
                    className={`border rounded-xl p-3 cursor-pointer transition-all ${
                      isSelected 
                        ? "border-[#8A1538] bg-[#8A1538]/5 shadow-md" 
                        : isAnySelected 
                          ? "border-slate-100 bg-white opacity-45 hover:opacity-75" 
                          : "border-slate-200 hover:border-slate-300 bg-white"
                    }`}
                  >
                    <div className="flex justify-between items-center mb-1">
                      <div className="flex items-center gap-2">
                        <span 
                          className="w-3 h-3 rounded-full shrink-0" 
                          style={{ background: colors[cNum] }}
                        />
                        <span className="text-xs font-bold text-slate-800 truncate max-w-[130px]">{cluster.name || `Cluster ${cNum}`}</span>
                      </div>
                      <span className="text-[10px] font-extrabold bg-slate-100 text-slate-600 rounded-full px-2 py-0.5">
                        {cluster.subcategory_count} subs
                      </span>
                    </div>

                    <div className="grid grid-cols-4 gap-1 text-[9.5px] text-slate-500 font-semibold mb-2">
                      <div>Count: <b className="text-slate-700 font-bold">{cluster.total_tickets}</b></div>
                      <div>CSAT: <b className="text-amber-600 font-bold">{cluster.avg_csat}</b></div>
                      <div>Res: <b className="text-slate-700 font-bold">{cluster.avg_resolution_time_hrs}h</b></div>
                      <div>Breach: <b className="text-red-600 font-bold">{cluster.avg_breach_rate_pct}%</b></div>
                    </div>

                    {isSelected && (
                      <div className="mt-2 pt-2 border-t border-slate-200/60 text-[11px] leading-relaxed text-slate-600 animate-in slide-in-from-top-1 duration-150">
                        <p className="italic text-slate-500 mb-2">{cluster.description}</p>
                        <div className="bg-[#8A1538]/5 border border-[#8A1538]/10 rounded-lg p-2 text-[#8A1538] font-bold">
                          <span className="text-[9.5px] uppercase tracking-wide block text-[#8A1538]/70 font-extrabold">AI Recommendation:</span>
                          {cluster.recommendation}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* agent leaderboard */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm mb-2">
        <div className="px-4 py-2.5 border-b border-slate-200"><span className="text-[11px] font-bold tracking-wide text-slate-500">AGENT PERFORMANCE</span></div>
        <div className="overflow-x-auto">
          <table className="w-full text-[12px]">
            <thead><tr className="text-[10px] uppercase tracking-wide text-slate-400">
              <th className="text-left font-bold px-4 py-2 border-b border-slate-100">Agent</th>
              <th className="text-left font-bold px-3 py-2 border-b border-slate-100">CSAT</th>
              <th className="text-left font-bold px-3 py-2 border-b border-slate-100">Avg resolution</th>
              <th className="text-left font-bold px-3 py-2 border-b border-slate-100">Resolved (6mo)</th>
              <th className="text-left font-bold px-3 py-2 border-b border-slate-100">SLA breach</th>
              <th className="text-left font-bold px-3 py-2 border-b border-slate-100">Load</th>
            </tr></thead>
            <tbody>
              {agents.map((a: any) => (
                <tr key={a.name} className="border-b border-slate-50 last:border-0">
                  <td className="px-4 py-2.5"><div className="font-bold text-slate-700">{a.name}</div><div className="text-[11px] text-slate-400">{a.team} &middot; {a.specialisation}</div></td>
                  <td className="px-3 py-2.5 font-bold text-amber-600">{a.csat != null ? a.csat + "*" : "-"}</td>
                  <td className="px-3 py-2.5 text-slate-600">{a.avg_resolution_hrs != null ? a.avg_resolution_hrs + "h" : "-"}</td>
                  <td className="px-3 py-2.5 text-slate-600">{a.resolved_6mo}</td>
                  <td className={`px-3 py-2.5 font-semibold ${a.breach_pct > 15 ? "text-red-600" : "text-slate-600"}`}>{a.breach_pct != null ? a.breach_pct + "%" : "-"}</td>
                  <td className="px-3 py-2.5"><span className={`text-[10.5px] font-extrabold rounded px-2 py-0.5 ${a.utilisation_pct >= 85 ? "bg-red-100 text-red-700" : a.utilisation_pct >= 65 ? "bg-amber-100 text-amber-700" : "bg-emerald-50 text-emerald-700"}`}>{a.utilisation_pct}%</span></td>
                </tr>
              ))}
              {agents.length === 0 && <tr><td colSpan={6} className="text-center text-slate-400 text-xs py-4">No agent data.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
});

// 6. Workflow Recap View
const WorkflowRecapView = () => {
  const steps = [
    { num: 1, title: "Employee raises query", desc: "User describes issue via chat portal (Arabic or English)" },
    { num: 2, title: "Self-Service Attempt", desc: "RAG searches KB text files - resolving ~68% automatically" },
    { num: 3, title: "Auto-Triage", desc: "Classified, prioritized, and routed dynamically without human wait" },
    { num: 4, title: "Smart Assignment", desc: "Assigned automatically to active technician with lowest workload" },
    { num: 5, title: "SLA Monitor", desc: "Real-time watchdog calculates risk and predicts breaches" },
    { num: 6, title: "Resolve & Close", desc: "Tickets are marked closed, updating agent workloads and operational stats" }
  ];

  return (
    <div className="flex flex-col h-full bg-white border border-slate-200/80 rounded-xl p-5 overflow-y-auto custom-scrollbar">
      <h4 className="text-sm font-semibold text-slate-800 mb-6 pb-3 border-b border-slate-200/60 flex items-center gap-2">
        <Network className="w-5 h-5 text-amber-600" />
        IT Support Agent Flow Architecture
      </h4>
      
      <div className="max-w-xl mx-auto space-y-4">
        {steps.map((s, idx) => (
          <div key={s.num} className="flex gap-4">
            <div className="flex flex-col items-center">
              <div className="w-8 h-8 rounded-full bg-[#8A1538] text-white text-xs font-bold flex items-center justify-center border border-[#d4af37]/30 shadow-md">
                {s.num}
              </div>
              {idx < steps.length - 1 && (
                <div className="w-0.5 bg-[#8A1538]/10 flex-1 my-1" />
              )}
            </div>
            <div className="pt-0.5 pb-4">
              <h5 className="text-sm font-semibold text-slate-800">{s.title}</h5>
              <p className="text-xs text-slate-500 mt-1">{s.desc}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const SUPPORT_AGENTS = [
  { agent_id: "AGT-001", full_name: "Mohammed Al-Rashid", team_level: "L1 Support", specialisation: "Network & Connectivity" },
  { agent_id: "AGT-002", full_name: "Layla Nasser", team_level: "L1 Support", specialisation: "Email & Communication" },
  { agent_id: "AGT-003", full_name: "Ahmed Ibrahim", team_level: "L1 Support", specialisation: "Hardware" },
  { agent_id: "AGT-004", full_name: "Hassan Yusuf", team_level: "L1 Support", specialisation: "Software & Applications" },
  { agent_id: "AGT-005", full_name: "Sara Al-Mansoori", team_level: "L1 Support", specialisation: "Onboarding & Offboarding" },
  { agent_id: "AGT-006", full_name: "Khalid Al-Farsi", team_level: "L2 Support", specialisation: "Network & Connectivity" },
  { agent_id: "AGT-007", full_name: "Noura Al-Hajri", team_level: "L2 Support", specialisation: "Core Banking System" },
  { agent_id: "AGT-008", full_name: "Omar Bin Saeed", team_level: "L2 Support", specialisation: "Security" },
  { agent_id: "AGT-009", full_name: "Fatima Al-Zaabi", team_level: "L2 Support", specialisation: "Hardware" },
  { agent_id: "AGT-010", full_name: "Tariq Al-Sulaiti", team_level: "L3 Support", specialisation: "Core Banking System" }
];

const TicketDetailModal = ({ ticket, onClose, onInvokeTool, onUpdateSuccess }: any) => {
  const [status, setStatus] = useState(ticket.status || "Open");
  const [priority, setPriority] = useState(ticket.priority || "P3");
  const [severity, setSeverity] = useState(ticket.severity || "Medium");
  const [assignedTo, setAssignedTo] = useState(ticket.assigned_to || "");
  const [slaTargetHrs, setSlaTargetHrs] = useState(ticket.sla_target_hrs || 24.0);
  const [category, setCategory] = useState(ticket.category || "");
  const [subCategory, setSubCategory] = useState(ticket.sub_category || "");
  const [categoriesMap, setCategoriesMap] = useState<Record<string, string[]>>({});
  const [updating, setUpdating] = useState(false);
  const [updateError, setUpdateError] = useState<string | null>(null);
  const [updateSuccessMsg, setUpdateSuccessMsg] = useState<string | null>(null);

  const [activities, setActivities] = useState<any[]>([]);
  const [loadingActivities, setLoadingActivities] = useState(false);
  const [commentText, setCommentText] = useState("");
  const [authorName, setAuthorName] = useState("Support Agent");

  useEffect(() => {
    onInvokeTool("get_service_desk_categories", {})
      .then((res: any) => {
        if (res && typeof res === "object") {
          const updated = { ...res };
          if (ticket.category && !updated[ticket.category]) {
            updated[ticket.category] = [ticket.sub_category].filter(Boolean);
          } else if (ticket.category && ticket.sub_category && !updated[ticket.category].includes(ticket.sub_category)) {
            updated[ticket.category] = [...updated[ticket.category], ticket.sub_category];
          }
          setCategoriesMap(updated);
        }
      })
      .catch((err: any) => console.error("Failed to load categories map:", err));
  }, [onInvokeTool, ticket.category, ticket.sub_category]);

  const handleCategoryChange = (cat: string) => {
    setCategory(cat);
    const subs = categoriesMap[cat] || [];
    setSubCategory(subs[0] || "");
  };

  const fetchActivities = useCallback(async () => {
    setLoadingActivities(true);
    try {
      const list = await onInvokeTool("get_service_desk_ticket_activities", { ticket_id: ticket.ticket_id });
      setActivities(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingActivities(false);
    }
  }, [ticket.ticket_id, onInvokeTool]);

  useEffect(() => {
    fetchActivities();
  }, [fetchActivities]);

  const handleAddComment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!commentText.trim() || !authorName.trim()) return;
    try {
      await onInvokeTool("add_service_desk_ticket_comment", {
        ticket_id: ticket.ticket_id,
        author: authorName.trim(),
        comment: commentText.trim()
      });
      setCommentText("");
      fetchActivities();
    } catch (err) {
      console.error(err);
    }
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    setUpdating(true);
    setUpdateError(null);
    setUpdateSuccessMsg(null);

    const agent = SUPPORT_AGENTS.find(a => a.full_name === assignedTo);
    const agentId = agent ? agent.agent_id : "";

    try {
      const resp = await onInvokeTool("update_service_desk_ticket", {
        ticket_id: ticket.ticket_id,
        status: status,
        priority: priority,
        severity: severity,
        assigned_to: assignedTo,
        agent_id: agentId,
        sla_target_hrs: slaTargetHrs,
        category: category,
        sub_category: subCategory
      });

      if (resp.status === "success") {
        setUpdateSuccessMsg("Ticket updated successfully!");
        onUpdateSuccess();
        setTimeout(() => {
          onClose();
        }, 500);
      } else {
        setUpdateError(resp.message || "Failed to update ticket.");
      }
    } catch (err: any) {
      console.error(err);
      setUpdateError(err.message || "Network error while updating ticket.");
    } finally {
      setUpdating(false);
    }
  };

  const getStatusBadgeClass = (s: string) => {
    switch (s) {
      case "Open": return "bg-sky-100 text-sky-800 border border-sky-200/60";
      case "In Progress": return "bg-amber-100 text-amber-800 border border-amber-200/60";
      case "Resolved": return "bg-emerald-100 text-emerald-800 border border-emerald-200/60";
      case "Closed": return "bg-slate-100 text-slate-800 border border-slate-200/60";
      default: return "bg-slate-100 text-slate-800 border border-slate-200/60";
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[1010] flex items-center justify-center p-4">
      <div className="absolute inset-0" onClick={onClose} />
      
      <div className="relative bg-white rounded-2xl w-[640px] max-h-[90vh] shadow-2xl overflow-hidden border border-slate-200/85 flex flex-col font-sans animate-in zoom-in-95 duration-200 z-[1020]">
        
        <div className="px-5 py-4 bg-[#8A1538] border-b border-[#d4af37]/30 flex justify-between items-center text-white shrink-0">
          <div className="flex items-center gap-2">
            <span className="text-[10px] tracking-widest font-mono uppercase bg-[#d4af37]/20 border border-[#d4af37]/40 px-2 py-0.5 rounded text-[#d4af37] font-semibold">
              {ticket.ticket_id}
            </span>
            <span className="text-xs font-semibold text-slate-200">QCB Support Log Detail</span>
          </div>
          <button 
            onClick={onClose}
            className="p-1 hover:bg-white/10 rounded-md transition-colors text-slate-200 hover:text-white"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-5 custom-scrollbar">
          
          <div className="flex items-start justify-between gap-4">
            <div className="space-y-1">
              <h3 className="text-base font-bold text-slate-900 leading-snug">{ticket.short_description}</h3>
              <p className="text-[11px] text-slate-500 font-semibold uppercase tracking-wider flex items-center gap-1.5">
                <Folder className="w-3.5 h-3.5 text-slate-400" />
                {category} &gt; {subCategory}
              </p>
            </div>
            <span className={`text-xs px-2.5 py-1 rounded-full font-bold uppercase ${getStatusBadgeClass(ticket.status)}`}>
              {ticket.status}
            </span>
          </div>

          <div className="bg-slate-50 border border-slate-200/60 rounded-xl p-4 space-y-1.5">
            <span className="text-[9px] font-bold text-slate-400 uppercase tracking-widest block">Issue Description</span>
            <p className="text-xs text-slate-700 leading-relaxed whitespace-pre-wrap font-sans">
              {ticket.description || "No description provided."}
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4 border-t border-slate-100 pt-4 text-xs">
            <div className="space-y-1">
              <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wider block">Raised By</span>
              <div className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg border border-slate-100">
                <User className="w-4 h-4 text-slate-500 shrink-0" />
                <div>
                  <p className="font-semibold text-slate-800">{ticket.raised_by_name || "Demo User"}</p>
                  <p className="text-[10px] text-slate-500">{ticket.department || "Operations"}</p>
                </div>
              </div>
            </div>

            <div className="space-y-1">
              <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wider block">Opened On</span>
              <div className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg border border-slate-100">
                <Calendar className="w-4 h-4 text-slate-500 shrink-0" />
                <div>
                  <p className="font-semibold text-slate-800">{ticket.raised_date || "2026-06-05"}</p>
                  <p className="text-[10px] text-slate-500">{ticket.raised_time || "12:00:00"}</p>
                </div>
              </div>
            </div>
          </div>

          <form onSubmit={handleUpdate} className="space-y-4 border-t border-slate-100 pt-4">
            <h4 className="text-[10px] font-bold text-slate-400 uppercase tracking-widest block">Interactive Reassignment &amp; Updates</h4>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-1">Category</label>
                <select 
                  value={category}
                  onChange={e => handleCategoryChange(e.target.value)}
                  className="w-full bg-slate-50 text-xs text-slate-800 rounded px-3 py-2 border border-slate-200 focus:outline-none focus:border-[#8A1538]"
                >
                  <option value="">Select Category</option>
                  {Object.keys(categoriesMap).map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-1">Subcategory</label>
                <select 
                  value={subCategory}
                  onChange={e => setSubCategory(e.target.value)}
                  className="w-full bg-slate-50 text-xs text-slate-800 rounded px-3 py-2 border border-slate-200 focus:outline-none focus:border-[#8A1538]"
                >
                  <option value="">Select Subcategory</option>
                  {(categoriesMap[category] || []).map(sub => (
                    <option key={sub} value={sub}>{sub}</option>
                  ))}
                </select>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-1">Assigned Technician</label>
                <select 
                  value={assignedTo}
                  onChange={e => setAssignedTo(e.target.value)}
                  className="w-full bg-slate-50 text-xs text-slate-800 rounded px-3 py-2 border border-slate-200 focus:outline-none focus:border-[#8A1538]"
                >
                  <option value="">Unassigned</option>
                  {SUPPORT_AGENTS.map(a => (
                    <option key={a.agent_id} value={a.full_name}>
                      {a.full_name} ({a.team_level})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-1">Ticket Status</label>
                <select 
                  value={status}
                  onChange={e => setStatus(e.target.value)}
                  className="w-full bg-slate-50 text-xs text-slate-800 rounded px-3 py-2 border border-slate-200 focus:outline-none focus:border-[#8A1538]"
                >
                  <option value="Open">Open</option>
                  <option value="In Progress">In Progress</option>
                  <option value="Resolved">Resolved</option>
                  <option value="Closed">Closed</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-1">Severity</label>
                <select 
                  value={severity}
                  onChange={e => setSeverity(e.target.value)}
                  className="w-full bg-slate-50 text-xs text-slate-800 rounded px-3 py-2 border border-slate-200 focus:outline-none focus:border-[#8A1538]"
                >
                  <option value="Critical">Critical</option>
                  <option value="High">High</option>
                  <option value="Medium">Medium</option>
                  <option value="Low">Low</option>
                </select>
              </div>

              <div>
                <label className="block text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-1">Priority</label>
                <select 
                  value={priority}
                  onChange={e => setPriority(e.target.value)}
                  className="w-full bg-slate-50 text-xs text-slate-800 rounded px-3 py-2 border border-slate-200 focus:outline-none focus:border-[#8A1538]"
                >
                  <option value="P1">P1 Critical</option>
                  <option value="P2">P2 High</option>
                  <option value="P3">P3 Medium</option>
                  <option value="P4">P4 Low</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-1">SLA Target (Hours)</label>
                <input 
                  type="number" 
                  step="0.5"
                  min="0.5"
                  value={slaTargetHrs}
                  onChange={e => setSlaTargetHrs(parseFloat(e.target.value) || 0)}
                  className="w-full bg-slate-50 text-xs text-slate-800 rounded px-3 py-2 border border-slate-200 focus:outline-none focus:border-[#8A1538]"
                />
              </div>
            </div>

            {updateError && (
              <div className="bg-red-50 text-red-700 p-2.5 rounded-lg border border-red-200/60 text-xs flex items-center gap-1.5 animate-in fade-in">
                <AlertTriangle className="w-4 h-4 text-red-500 shrink-0" />
                <span>{updateError}</span>
              </div>
            )}
            {updateSuccessMsg && (
              <div className="bg-emerald-50 text-emerald-700 p-2.5 rounded-lg border border-emerald-200/60 text-xs flex items-center gap-1.5 animate-in fade-in">
                <CheckCircle className="w-4 h-4 text-emerald-500 shrink-0" />
                <span>{updateSuccessMsg}</span>
              </div>
            )}

            <div className="flex gap-3 justify-end pt-2 border-t border-slate-100 shrink-0">
              <button 
                type="button" 
                onClick={onClose}
                disabled={updating}
                className="px-4 py-2 border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 text-xs font-semibold rounded-lg shadow-sm"
              >
                Cancel
              </button>
              <button 
                type="submit" 
                disabled={updating}
                className="px-5 py-2 bg-[#8A1538] hover:bg-[#a01c43] text-white text-xs font-bold rounded-lg shadow-md flex items-center gap-1.5 disabled:opacity-60"
              >
                {updating && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                Save Updates
              </button>
            </div>
          </form>

          {/* Activities and Comments Section */}
          <div className="border-t border-slate-200 pt-5 space-y-4">
            <h4 className="text-[11px] font-bold text-slate-500 uppercase tracking-widest flex items-center gap-1.5">
              <MessageSquare className="w-4 h-4 text-slate-400" />
              Activity History &amp; Comments
            </h4>

            {/* Activities List */}
            <div className="bg-slate-50/50 rounded-xl border border-slate-100 p-4 max-h-[200px] overflow-y-auto space-y-3 custom-scrollbar">
              {loadingActivities ? (
                <div className="flex items-center justify-center py-4 text-slate-400 text-xs gap-1.5">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  <span>Loading history...</span>
                </div>
              ) : activities.length === 0 ? (
                <div className="text-center text-slate-400 text-xs py-4">
                  No activity logs recorded yet.
                </div>
              ) : (
                <div className="space-y-3">
                  {activities.map((act) => {
                    const isComment = act.activity_type === "comment";
                    const formattedDate = act.created_at ? new Date(act.created_at).toLocaleString() : "";
                    return (
                      <div key={act.id} className="flex gap-2.5 items-start text-xs text-slate-600 leading-normal">
                        <div className={`w-6 h-6 rounded-full shrink-0 flex items-center justify-center ${isComment ? 'bg-amber-100 text-amber-700' : 'bg-slate-200 text-slate-500'}`}>
                          {isComment ? <MessageSquare className="w-3.5 h-3.5" /> : <Clock className="w-3.5 h-3.5" />}
                        </div>
                        <div className="flex-1">
                          <div className="flex justify-between items-center mb-0.5">
                            <span className="font-bold text-slate-700">{isComment ? act.author : "System"}</span>
                            <span className="text-[9px] text-slate-400 font-mono">{formattedDate}</span>
                          </div>
                          <p className={`p-2 rounded-lg ${isComment ? 'bg-white border border-slate-100 shadow-sm text-slate-700 font-sans' : 'bg-slate-100/60 text-slate-500 italic font-mono text-[11px]'}`}>
                            {act.message}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Comment Form */}
            <div className="bg-slate-50 border border-slate-200/60 rounded-xl p-4 space-y-3">
              <span className="text-[9px] font-bold text-slate-400 uppercase tracking-widest block">Add Support Comment</span>
              <div className="flex gap-3">
                <div className="w-1/3">
                  <label className="block text-[9px] font-semibold text-slate-500 uppercase tracking-wider mb-1">Your Name</label>
                  <input 
                    type="text"
                    value={authorName}
                    onChange={e => setAuthorName(e.target.value)}
                    placeholder="e.g. Agent 1"
                    className="w-full bg-white text-xs text-slate-800 rounded px-2.5 py-1.5 border border-slate-200 focus:outline-none focus:border-[#8A1538]"
                  />
                </div>
                <div className="flex-1">
                  <label className="block text-[9px] font-semibold text-slate-500 uppercase tracking-wider mb-1">Comment</label>
                  <div className="flex gap-2">
                    <input 
                      type="text"
                      value={commentText}
                      onChange={e => setCommentText(e.target.value)}
                      placeholder="Type comment details..."
                      className="flex-1 bg-white text-xs text-slate-800 rounded px-2.5 py-1.5 border border-slate-200 focus:outline-none focus:border-[#8A1538]"
                    />
                    <button 
                      type="button"
                      onClick={handleAddComment}
                      className="px-4 py-1.5 bg-slate-700 hover:bg-slate-800 text-white font-semibold rounded-lg text-xs transition-colors shrink-0"
                    >
                      Post
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

// -------------------------------------------------------------
// Main Component
// -------------------------------------------------------------

const ServiceDeskWorkflowApp: React.FC<ServiceDeskWorkflowAppProps> = ({ isOpen, onClose, onInvokeTool }) => {
  const [activeTab, setActiveTab] = useState("triage");
  const [isMaximized, setIsMaximized] = useState(false);
  const [prefillTicket, setPrefillTicket] = useState<{ subject: string; desc: string } | null>(null);
  
  // Shared state for Active IT support tickets
  const [tickets, setTickets] = useState<any[]>([]);
  const [loadingTickets, setLoadingTickets] = useState(false);
  const [selectedTicketId, setSelectedTicketId] = useState<string | null>(null);

  const fetchTickets = useCallback(async () => {
    setLoadingTickets(true);
    try {
      const list = await onInvokeTool("list_service_desk_tickets", {});
      setTickets(Array.isArray(list) ? list : []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingTickets(false);
    }
  }, [onInvokeTool]);

  useEffect(() => {
    if (isOpen) {
      fetchTickets();
    }
  }, [isOpen, fetchTickets]);

  const [selectedTicket, setSelectedTicket] = useState<any | null>(null);

  useEffect(() => {
    if (selectedTicketId) {
      const found = tickets.find(t => t.ticket_id === selectedTicketId);
      if (found) {
        setSelectedTicket(found);
      } else {
        onInvokeTool("list_service_desk_tickets", { ticket_id: selectedTicketId })
          .then((res: any) => {
            if (res && res.length > 0) {
              setSelectedTicket(res[0]);
            }
          })
          .catch((err: any) => console.error(err));
      }
    } else {
      setSelectedTicket(null);
    }
  }, [selectedTicketId, tickets, onInvokeTool]);

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop Overlay */}
      <div 
        className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[999] transition-opacity" 
        onClick={onClose}
      />
      
      {/* Modal Dialog */}
      <div className={`fixed z-[1000] bg-white text-slate-800 shadow-2xl flex flex-col font-sans transition-all duration-300 ease-in-out border border-slate-200/80 ${
        isMaximized 
          ? "top-2 left-2 right-2 bottom-2 rounded-2xl" 
          : "top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[1050px] h-[620px] rounded-2xl"
      }`}>
        {/* Premium Header */}
        <div className="px-5 py-4 bg-slate-50 border-b border-slate-200/80 flex items-center justify-between shrink-0 rounded-t-2xl">
          <div className="flex items-center gap-3">
            {/* QCB Logo Image */}
            <img 
              src="/qcb-logo.jpg" 
              alt="QCB Logo" 
              className="w-10 h-10 object-contain rounded-xl border border-slate-200/80 shadow-md transform rotate-3 bg-white" 
            />
            <div>
              <h2 className="text-sm font-bold text-slate-800 tracking-wide uppercase">Qatar Central Bank (QCB)</h2>
              <p className="text-[10px] text-slate-500 font-semibold uppercase tracking-wider">Technical Support Section · Service Desk</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button 
              onClick={() => setIsMaximized(!isMaximized)} 
              className="p-1.5 hover:bg-slate-200/60 rounded-md text-slate-500 hover:text-slate-800 transition-colors"
            >
              {isMaximized ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
            </button>
            <button 
              onClick={onClose} 
              className="p-1.5 hover:bg-slate-200/60 rounded-md text-slate-500 hover:text-slate-800 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Content layout */}
        <div className="flex-1 flex overflow-hidden">
          {/* Left Side Navigation tabs */}
          <div className="w-56 bg-slate-50 border-r border-slate-200/80 flex flex-col p-3 shrink-0 justify-between">
            <div className="space-y-1">
              <span className="block px-3 text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2">Capabilities</span>
              
              <button
                onClick={() => setActiveTab("triage")}
                className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-xs font-semibold transition-all ${
                  activeTab === "triage"
                    ? "bg-[#8A1538] text-white shadow-md border-l-2 border-[#d4af37]"
                    : "text-slate-500 hover:text-slate-800 hover:bg-slate-200/60"
                }`}
              >
                <ArrowUpRight className="w-4 h-4 shrink-0" />
                Live Queue & Triage
              </button>
              <button
                onClick={() => setActiveTab("ops")}
                className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-xs font-semibold transition-all ${
                  activeTab === "ops"
                    ? "bg-[#8A1538] text-white shadow-md border-l-2 border-[#d4af37]"
                    : "text-slate-500 hover:text-slate-800 hover:bg-slate-200/60"
                }`}
              >
                <BarChart3 className="w-4 h-4 shrink-0" />
                Operations Dashboard
              </button>

              {/* Legacy tabs — hidden for now, kept in code for later re-enable */}
              {false && (
                <>
                  <button onClick={() => setActiveTab("self_service")} className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-xs font-semibold text-slate-500">
                    <MessageSquare className="w-4 h-4 shrink-0" /> Self-service desk
                  </button>
                  <button onClick={() => setActiveTab("kb")} className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-xs font-semibold text-slate-500">
                    <BookOpen className="w-4 h-4 shrink-0" /> Knowledge base
                  </button>
                  <button onClick={() => setActiveTab("sla")} className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-xs font-semibold text-slate-500">
                    <Clock className="w-4 h-4 shrink-0" /> SLA watchdog
                  </button>
                  <button onClick={() => setActiveTab("recap")} className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-xs font-semibold text-slate-500">
                    <Network className="w-4 h-4 shrink-0" /> Workflow recap
                  </button>
                </>
              )}
            </div>
            
            <div className="px-3 py-2 border-t border-slate-200/60 text-[9px] text-slate-400 text-center font-mono uppercase tracking-wider">
              Powered by EngageIQ
            </div>
          </div>

          {/* Dynamic Detail Panel */}
          <div className="flex-1 bg-white p-5 overflow-hidden relative">
            <div className={`h-full ${activeTab === "self_service" ? "" : "hidden"}`}>
              <SelfServiceView 
                onInvokeTool={onInvokeTool} 
                onSwitchTab={setActiveTab} 
                setPrefillTicket={setPrefillTicket} 
                onSelectTicket={setSelectedTicketId}
              />
            </div>
            <div className={`h-full ${activeTab === "triage" ? "" : "hidden"}`}>
              <TriageView 
                onInvokeTool={onInvokeTool} 
                prefillTicket={prefillTicket} 
                clearPrefill={() => setPrefillTicket(null)} 
                tickets={tickets}
                loading={loadingTickets}
                fetchTickets={fetchTickets}
                onSelectTicket={setSelectedTicketId}
              />
            </div>
            <div className={`h-full ${activeTab === "kb" ? "" : "hidden"}`}>
              <KnowledgeBaseView onInvokeTool={onInvokeTool} />
            </div>
            <div className={`h-full ${activeTab === "sla" ? "" : "hidden"}`}>
              <SLAWatchdogView 
                onInvokeTool={onInvokeTool} 
                onSelectTicket={setSelectedTicketId}
              />
            </div>
            <div className={`h-full ${activeTab === "ops" ? "" : "hidden"}`}>
              <OpsDashboardView onInvokeTool={onInvokeTool} onSelectTicket={setSelectedTicketId} />
            </div>
            <div className={`h-full ${activeTab === "recap" ? "" : "hidden"}`}>
              <WorkflowRecapView />
            </div>
          </div>
        </div>
      </div>

      {selectedTicket && (
        <TicketDetailModal 
          ticket={selectedTicket} 
          onClose={() => setSelectedTicketId(null)} 
          onInvokeTool={onInvokeTool}
          onUpdateSuccess={() => {
            fetchTickets();
            window.dispatchEvent(new CustomEvent("ticketUpdated"));
          }}
        />
      )}
    </>
  );
};

export default ServiceDeskWorkflowApp;
